import { FC, useCallback, useState } from 'react';
import { useRouter } from 'shared-routing';
import { AccommodationDetailTemplate, DetailModal } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import { ContentParser } from '@common/components/ContentParser';
import { Routes } from '@common/constants';
import { useLoadingState } from '@common/hooks';
import useDetailModalLabels from '@common/hooks/useDetailModalLabels';
import useRoomCapacityText from '@common/hooks/useRoomCapacityText';
import { analytics } from '@features/analytics';
import { UpgradeItemVersion } from '@features/List/BookableList/UpgradeItem';
import { RoomRateConfigurationWizard } from '@features/RoomRateConfigurationWizard';
import { isInlineBookingStep } from '@features/RoomRateConfigurationWizard/FormSteps/constants';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useJourneyPickerAvailability } from '@features/JourneyPickerV2/useJourneyPickerAvailability';
import { moreRoomsAvailableThanInBasket } from '@features/rates/helpers/moreRoomsAvailableThanInBasket.helper';
import { useDenialAnalytics } from '@features/rates/helpers/useDenialAnalytics.helper';
import { formatStayDates, useNextAvailableStay } from '@features/rooms';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import rewriteHttp from '@utils/rewriteHttp';
import { RoomUpgrade } from 'shared-types/Rate';

import { RoomDetailV2Props } from './RoomDetailV2.types';
import { useRoomDetail } from './useRoomDetail';

export const RoomDetailV2: FC<RoomDetailV2Props> = ({
  journeyParams,
  changeJourneyParam,
  language,
  property,
  roomIdsInBasket,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const bookingStep = queryParamToString(router.query.bookingStep);
  const bookedRateId = queryParamToString(router.query.rate);

  const {
    roomId,
    staticRoom,
    staticRoomError,
    availableRates,
    rateCodeNotice,
    items,
    fallbackImageUrl,
    urgencyLabel,
    numberOfNights,
    isRoomOutOfStock,
    allAvailableRoomsInBasket,
    isFetching,
    isLoading,
  } = useRoomDetail(journeyParams, roomIdsInBasket, language, property);
  const { isInitialLoading, isRefetching } = useLoadingState(
    isLoading || isFetching,
    !isLoading && !!staticRoom.title
  );

  const [selectedRateId, setSelectedRateId] = useState<string | undefined>(undefined);
  const effectiveSelectedId = availableRates.some((rate) => rate.rateId === selectedRateId)
    ? selectedRateId
    : availableRates[0]?.rateId;

  const [detailRateId, setDetailRateId] = useState<string | undefined>(undefined);
  const detailRate = availableRates.find((rate) => rate.rateId === detailRateId);
  const [isAmenitiesOpen, setIsAmenitiesOpen] = useState(false);
  const detailModalLabels = useDetailModalLabels();

  const { availability, isLoadingAvailability, onMonthChange } = useJourneyPickerAvailability({
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    initialMonth: journeyParams.fromDate?.format('YYYY-MM-DD'),
  });

  const isNotFound = !!staticRoomError && !isLoading;
  const isUnavailable =
    !staticRoomError && !isLoading && !isFetching && (items.length === 0 || isRoomOutOfStock);

  const { nextStay, isLoading: isLoadingNextStay } = useNextAvailableStay({
    roomCode: roomId,
    fromDate: journeyParams.fromDate,
    nights: numberOfNights,
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    isUnavailable: isUnavailable && !allAvailableRoomsInBasket,
  });

  useDenialAnalytics({
    isFetching,
    hasAvailability: availableRates.length > 0 && !isRoomOutOfStock,
    itemCategory: 'rooms',
    itemId: staticRoom.roomId,
    itemName: staticRoom.title,
    fromDate: journeyParams.fromDate,
    toDate: journeyParams.toDate,
  });

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      const adults = params.adults;
      const children = params.children ?? 0;

      changeJourneyParam('rooms', [{ roomNumber: 1, adults, children }]);
      changeJourneyParam('childAges', []);

      analytics.track({
        event: 'view_item',
        params: {
          adults,
          children,
          start_date: params.fromDate,
          end_date: params.toDate,
          items: [
            { item_category: 'rooms', item_id: staticRoom.roomId, item_name: staticRoom.title },
          ],
        },
      });
    },
    [changeJourneyParam, staticRoom.roomId, staticRoom.title]
  );

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const handleUseNextAvailable = useCallback(() => {
    if (!nextStay) return;

    changeJourneyParam('fromDate', nextStay.checkIn);
    changeJourneyParam('toDate', nextStay.checkOut);
    changeJourneyParam('spaDate', nextStay.checkIn);
  }, [changeJourneyParam, nextStay]);

  const handleRateCodeChange = useCallback(
    (code: string) => {
      changeJourneyParam('promoRateCode', code || undefined);
    },
    [changeJourneyParam]
  );

  const handleBack = useCallback(() => {
    router.push({ pathname: Routes.ROOM_LIST });
  }, [router]);

  const reserveRate = useCallback(
    (rateId?: string) => {
      const rate = availableRates.find((availableRate) => availableRate.rateId === rateId);
      if (!rate) return;

      const promoRateCode = queryParamToString(router.query.promoRateCode);
      const shouldOfferUpgrade =
        !!rate.roomUpgrade &&
        moreRoomsAvailableThanInBasket(rate.roomUpgrade, roomIdsInBasket ?? []);

      router.push({
        pathname: Routes.RATE_BOOKING,
        query: {
          id: rate.rateId,
          room: roomId,
          bookingStep: shouldOfferUpgrade ? 'upgrade' : 'guestDetailsStep',
          fromDate: parseDateToString(journeyParams.fromDate),
          toDate: parseDateToString(journeyParams.toDate),
          spaDate: parseDateToString(journeyParams.fromDate),
          adults: String(journeyParams.adults),
          children: String(journeyParams.children),
          ...(promoRateCode ? { promoRateCode } : undefined),
        },
      });
    },
    [availableRates, journeyParams, roomId, roomIdsInBasket, router]
  );

  const handleReserve = useCallback(() => {
    reserveRate(effectiveSelectedId);
  }, [effectiveSelectedId, reserveRate]);

  const journeyBar = (
    <JourneyPickerV2Connected
      imageOverlay
      dateMode="range"
      dateContext="stay"
      showGuests
      showRate
      rateCode={journeyParams.promoRateCode}
      onRateCodeChange={handleRateCodeChange}
      numberOfMonths={1}
      availability={availability}
      isLoadingAvailability={isLoadingAvailability}
      onMonthChange={onMonthChange}
      onSearchComplete={handleSearchComplete}
    />
  );

  const roomImages = (staticRoom.images ?? [])
    .map((image) => image.url)
    .filter((url): url is string => !!url);
  const fallbackImages = fallbackImageUrl ? [fallbackImageUrl] : [];
  const gallerySources = roomImages.length ? roomImages : fallbackImages;
  const images = gallerySources.map((src, index) => ({
    src: rewriteHttp(src),
    alt:
      gallerySources.length > 1
        ? `${staticRoom.title} (${index + 1}/${gallerySources.length})`
        : staticRoom.title,
  }));

  const capacityText = useRoomCapacityText({
    maxAdults: staticRoom.maxAdults,
    maxChildren: staticRoom.maxChildren,
    maxOccupancy: staticRoom.maxOccupancy,
  });
  const metaLabels = capacityText ? [capacityText] : undefined;

  const upgrade = availableRates.find((rate) => rate.rateId === bookedRateId)?.roomUpgrade;
  const isUpgradeStep =
    bookingStep === 'upgrade' &&
    !!upgrade &&
    moreRoomsAvailableThanInBasket(upgrade, roomIdsInBasket ?? []);
  const guestStepLinkParams = { ...router.query, bookingStep: 'guestDetailsStep' };

  if (isUpgradeStep) {
    return (
      <UpgradeItemVersion
        trackingItem={{ id: staticRoom.roomId, name: staticRoom.title }}
        isOpen
        {...(upgrade as RoomUpgrade)}
        linkRoute={Routes.ROOM_BOOKING}
        linkParams={guestStepLinkParams}
        upgradeLinkParams={{ ...guestStepLinkParams, id: upgrade?.id }}
        language={language}
        numberOfNights={numberOfNights}
        originalRoom={{
          id: staticRoom.roomId,
          name: staticRoom.title,
          imageUrl: staticRoom.images?.[0]?.url,
        }}
      />
    );
  }

  const isMultiRoom = (journeyParams.rooms?.length ?? 0) > 1;
  const isV2BookingStep = isInlineBookingStep(bookingStep, isMultiRoom);

  const roomRateWizard =
    availableRates.length > 0 ? (
      <RoomRateConfigurationWizard
        bookingStep={bookingStep}
        language={language}
        metaEntity={
          {
            type: 'room',
            id: staticRoom.roomId,
            title: staticRoom.title,
            rates: availableRates,
          } as any
        }
        entity={{ type: 'rate', id: bookedRateId } as any}
      />
    ) : null;

  const displayedItems = isFetching || isRoomOutOfStock ? [] : items;

  const unavailable = isNotFound
    ? {
        title: t('room.noResults'),
        ctaLabel: t('app.backToRooms'),
        onCtaClick: handleBack,
      }
    : isUnavailable && !isLoadingNextStay
    ? {
        title: nextStay ? t('room.nextAvailableHeader') : t('room.noRatesHeader'),
        subtitle: nextStay
          ? t('room.nextAvailableInfo', formatStayDates(nextStay))
          : t('room.noRatesInfo'),
        ctaLabel: nextStay ? t('room.nextAvailableCta') : t('common.editSearch'),
        onCtaClick: nextStay ? handleUseNextAvailable : handleEditSearch,
      }
    : undefined;

  const showDetail = !(isV2BookingStep && roomRateWizard);

  return (
    <>
      {showDetail && (
        <>
          <AccommodationDetailTemplate
            title={staticRoom.title}
            images={images}
            items={displayedItems}
            showItemImages={false}
            urgencyLabel={urgencyLabel}
            notice={rateCodeNotice}
            metaLabels={metaLabels}
            selectedItemId={effectiveSelectedId}
            onItemSelect={setSelectedRateId}
            onSelectItem={setDetailRateId}
            selectItemLabel={t('common.moreDetails')}
            onReserve={handleReserve}
            reserveLabel={t('common.reserve')}
            showMoreItemsLabel={t('common.showMore')}
            showLessItemsLabel={t('common.showLess')}
            description={staticRoom.description || undefined}
            descriptionShowMoreLabel={t('common.showMore')}
            descriptionShowLessLabel={t('common.showLess')}
            modalTriggerLabel={staticRoom.amenities?.length ? t('room.amenities') : undefined}
            onModalTriggerClick={
              staticRoom.amenities?.length ? () => setIsAmenitiesOpen(true) : undefined
            }
            editSearchLabel={t('common.editSearch')}
            onEditSearch={handleEditSearch}
            previousImageLabel={t('aria.previousImage')}
            nextImageLabel={t('aria.nextImage')}
            goToImageLabel={(position, total) => t('aria.goToImage', { position, total })}
            onBack={handleBack}
            viewAllLabel={t('common.viewAll')}
            journeyBar={journeyBar}
            isLoading={isInitialLoading || isRefetching || isLoadingNextStay}
            unavailable={unavailable}
          />
          {detailRate && (
            <DetailModal
              open
              onClose={() => setDetailRateId(undefined)}
              title={detailRate.rateTitle}
              pricePerStay={
                detailRate.pricePerStay
                  ? {
                      amount: detailRate.pricePerStay.amount,
                      currencyCode: detailRate.pricePerStay.currencyCode,
                    }
                  : undefined
              }
              pricePerNight={
                detailRate.pricePerStay && numberOfNights > 0
                  ? {
                      amount: Math.round(detailRate.pricePerStay.amount / numberOfNights),
                      currencyCode: detailRate.pricePerStay.currencyCode,
                    }
                  : undefined
              }
              showTotalDue
              description={
                detailRate.rateDescription ? (
                  <ContentParser text={detailRate.rateDescription} escapeHtml={false} />
                ) : undefined
              }
              terms={
                detailRate.rateTerms ? (
                  <ContentParser text={detailRate.rateTerms} escapeHtml={false} />
                ) : undefined
              }
              onReserve={() => reserveRate(detailRate.rateId)}
              locale={language}
              labels={detailModalLabels}
            />
          )}
          {isAmenitiesOpen && (
            <DetailModal
              open
              onClose={() => setIsAmenitiesOpen(false)}
              title={t('room.amenities')}
              titleVariant="prominent"
              amenities={(staticRoom.amenities ?? []).map((amenity) => ({
                id: amenity,
                label: amenity,
              }))}
              amenitiesVariant="detailed"
              locale={language}
              labels={detailModalLabels}
            />
          )}
        </>
      )}
      {roomRateWizard}
    </>
  );
};
